import { useCallback, useMemo } from 'react';

import { useUrlState } from '@/shared/lib/hooks';

import {
  enabledCorridorTypes,
  enabledReports,
  parseMapLayers,
  toggleMapLayer,
  visibleLocationTypes
} from '../lib/map-layers.utils';
import { DEFAULT_MAP_LAYERS } from '../lib/map-layers.config';
import type { MapLayerId } from './types';

const DEFAULT_LAYERS_URL_STATE = { layers: DEFAULT_MAP_LAYERS.join(',') };

/**
 * Which map layers are lit. Kept in the URL next to the data filters so a shared
 * link restores the whole map, not just its filters.
 */
export function useMapLayers() {
  const [urlState, setUrlState] = useUrlState(DEFAULT_LAYERS_URL_STATE);

  const activeLayers = useMemo(() => parseMapLayers(urlState.layers), [urlState.layers]);

  const toggleLayer = useCallback(
    (id: MapLayerId) => setUrlState({ layers: toggleMapLayer(activeLayers, id).join(',') }),
    [activeLayers, setUrlState]
  );

  return {
    activeLayers,
    toggleLayer,
    reports: useMemo(() => enabledReports(activeLayers), [activeLayers]),
    corridorTypes: useMemo(() => enabledCorridorTypes(activeLayers), [activeLayers]),
    locationTypes: useMemo(() => visibleLocationTypes(activeLayers), [activeLayers])
  };
}
